import { v4 as uuidv4 } from "uuid";

/** The request fields that define "the same logical request" for dedup. */
export type RequestIdentity = Record<string, string | number | boolean>;

/**
 * A handle for the idempotency key of a single logical request.
 *
 * SDK mutations such as `transfersApi.createTransfer` require an `idempotencyKey`
 * that must stay **stable across renders and retries of the same logical
 * request** (so a replay after a client-side timeout is deduped server-side
 * instead of moving money twice), then be **destroyed once the request
 * succeeds** so a later, intentional repeat of the same request gets a fresh key.
 *
 * Construct one with the `requestIdentity` — the fields that make two requests
 * "the same" (e.g. amount + source + destination), excluding UI-only state. The
 * key is resolved from a process-wide map, so every instance built from the same
 * identity shares one `value`; `destroy()` forgets it so the next instance mints
 * a fresh one.
 *
 * ```ts
 * const idempotencyKey = new IdempotencyKey(requestIdentity);
 * transfer(
 *   { idempotencyKey: idempotencyKey.value, ... },
 *   { onSuccess: () => idempotencyKey.destroy() }
 * );
 * ```
 *
 * The serialized identity is used as the map key directly (not a lossy hash):
 * for money movement a hash collision could silently dedup two distinct requests,
 * so we avoid that risk entirely.
 */
export class IdempotencyKey {
  private static readonly keys = new Map<string, string>();

  private readonly serializedIdentity: string;
  readonly value: string;

  constructor(requestIdentity: RequestIdentity) {
    this.serializedIdentity = IdempotencyKey.serialize(requestIdentity);
    this.value = IdempotencyKey.resolve(this.serializedIdentity);
  }

  /** Forgets this key so the next `IdempotencyKey` for the same identity mints a new one. */
  destroy(): void {
    IdempotencyKey.keys.delete(this.serializedIdentity);
  }

  private static resolve(serializedIdentity: string): string {
    return (
      IdempotencyKey.keys.get(serializedIdentity) ??
      IdempotencyKey.generate(serializedIdentity)
    );
  }

  private static generate(serializedIdentity: string): string {
    const key = uuidv4();
    IdempotencyKey.keys.set(serializedIdentity, key);
    return key;
  }

  // Sort keys so field order doesn't matter: { a, b } and { b, a } serialize
  // identically and therefore share one idempotency key.
  private static serialize(requestIdentity: RequestIdentity): string {
    return Object.keys(requestIdentity)
      .sort()
      .map(key => `${key}=${requestIdentity[key]}`)
      .join("&");
  }
}
